import { HStack, Text, VStack, Box, Button, Tabs } from '@chakra-ui/react';
import {
  AddIcon,
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
  FileIcon,
  SearchIcon,
  ShareIcon,
} from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { DateCell } from 'components/common/Table/Cells/DateCell';
import { TextCell } from 'components/common/Table/Cells';
import { TextCellWithIcon } from 'components/common/Table/Cells/TextCellWithIcon';
import { Table } from 'components/common/Table/Table';
import { Pagination } from 'components/common/Table/Pagination';
import { Filters } from 'components/common/Table/Filters';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { TextCellProps } from 'components/common/Table/types/Cell';
import { createColumns } from 'utils/tableHelper';
import { WorksheetDistributionFilter } from 'types/Worksheets';
import { useModalService } from 'hooks/useModalService';
import { useNavigate } from 'react-router';
import {
  useGetWorksheets,
  useCreateWorksheet,
  useDeleteWorksheet,
  useDuplicateWorksheet,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { useState, useEffect } from 'react';
import { ModalVariant } from 'types/Modal';
import { ProtectedRoutes } from 'types/Routes';
import { SetURLSearchParams } from 'react-router-dom';
import { NoTableContent } from 'components/common/NoTableContent';
import { NoSearchResult } from 'components/common/NoSearchResult';
import { WorksheetMenu } from 'components/worksheet/WorksheetMenu';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { TabBar, TabBarVariant } from 'components/Article/TabBar';
import { useUser } from 'utils/auth';
import { TabData } from 'types/tab';
import { WorksheetData } from '../WorksheetDashboard';
import { OrganizationWorksheetsTab } from './OrganizationWorksheetsTab';

const worksheetColumns = createColumns<WorksheetData>()({
  title: {
    header: 'Titel',
    element: (props: TextCellProps) => (
      <TextCellWithIcon
        label={props.label}
        iconBg="#F0EDFE"
        icon={<NovaIcon icon={FileIcon} size="m" color="#4D3C9E" aria-hidden />}
        textStyle="body.s.medium"
        textColor="text.secondary"
      />
    ),
    sortDisabled: true,
  },
  participants: {
    header: 'Lernende',
    element: (props: TextCellProps) => (
      <TextCell
        lineClamp={2}
        label={props.label}
        textStyle="body.s.regular"
        textColor="text.secondary"
        whiteSpace="pre-line"
      />
    ),
    sortDisabled: true,
  },
  description: {
    header: 'Beschreibung',
    element: (props: TextCellProps) => (
      <TextCell
        label={props.label}
        textStyle="body.xs.regular"
        lineClamp="l"
        textColor="text.secondary"
        whiteSpace="pre-line"
      />
    ),
    sortDisabled: true,
  },
  createdAt: { header: 'Erstellt am', element: DateCell, sortDisabled: true },
  actionItem: {
    header: 'Aktion',
    sortDisabled: true,
  },
});

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface WorksheetsTabProps {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export const WorksheetsTab = ({
  searchParams,
  setSearchParams,
}: WorksheetsTabProps): JSX.Element => {
  const navigate = useNavigate();
  const modalService = useModalService();
  const { data: user } = useUser();

  // Initialize from URL params
  const filterFromUrl = searchParams.get('f') as WorksheetDistributionFilter;
  const pageFromUrl = searchParams.get('p');

  const [activeFilter, setActiveFilter] = useState<WorksheetDistributionFilter>(
    Object.values(WorksheetDistributionFilter).includes(filterFromUrl)
      ? filterFromUrl
      : WorksheetDistributionFilter.ALL
  );
  const [currentPage, setCurrentPage] = useState<number>(
    pageFromUrl ? parseInt(pageFromUrl, 10) : 1
  );
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('s') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(
    searchParams.get('s') || ''
  );
  const [worksheetView, setWorksheetView] = useState<'my' | 'organization'>(
    searchParams.get('wsv') === 'organization' ? 'organization' : 'my'
  );
  const worksheets = useGetWorksheets(activeFilter, currentPage, debouncedSearchQuery);
  const { mutateAsync: createWorksheet } = useCreateWorksheet();
  const { mutateAsync: deleteWorksheet } = useDeleteWorksheet();
  const { mutateAsync: duplicateWorksheet } = useDuplicateWorksheet();
  // Sync filters
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (searchQuery) {
      newParams.set('s', searchQuery);
    } else {
      newParams.delete('s');
    }

    if (activeFilter !== WorksheetDistributionFilter.ALL) {
      newParams.set('f', activeFilter);
    } else {
      newParams.delete('f');
    }

    if (currentPage > 1) {
      newParams.set('p', currentPage.toString());
    } else {
      newParams.delete('p');
    }

    if (worksheetView === 'organization') {
      newParams.set('wsv', worksheetView);
    } else {
      newParams.delete('wsv');
    }

    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeFilter, currentPage, worksheetView]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleWsClick = (id: string): void => {
    // Handle the click event for the worksheet
    // For example, navigate to the worksheet session page
    navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${id}`);
  };

  const handleWsDeleteClick = async (id: string): Promise<void> => {
    // Handle the delete action here
    const worksheet = worksheets.data?.data?.find((worksheet) => worksheet.id === id);

    modalService.show(ModalVariant.WORKSHEET_DELETE, {
      description: worksheet?.isDistributed
        ? `Möchtest du das Arbeitsblatt "${worksheet?.title}" wirklich löschen?\n\nWenn das Arbeitsblatt gelöscht wird, werden die Antworten der Auszubildenden ebenfalls gelöscht.`
        : `Möchtest du das Arbeitsblatt "${worksheet?.title}" wirklich löschen?`,
      onDelete: async () => {
        await deleteWorksheet(id);
        await worksheets.refetch();
      },
    });
  };

  const handleCreateWorksheet = async (): Promise<void> => {
    const worksheet = await createWorksheet({});
    navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${worksheet.id}`);
  };

  const handleWsDuplicateClick = async (id: string): Promise<void> => {
    await duplicateWorksheet({ id });
    await worksheets.refetch();
  };

  const handleWsShareClick = async (id: string): Promise<void> => {
    modalService.show(ModalVariant.WORKSHEET_SHARE, {
      worksheetId: id,
    });
  };

  const mappedWorksheets = worksheets.data?.data?.map((worksheet) => {
    return {
      ...worksheet,
      description: worksheet.description,
      participants: (() => {
        const parts = [];
        if (worksheet.distributionCount?.classes) {
          parts.push(
            `${worksheet.distributionCount.classes} Kurs${worksheet.distributionCount.classes !== 1 ? 'e' : ''}`
          );
        }
        if (worksheet.distributionCount?.guestStudents) {
          parts.push(
            `${worksheet.distributionCount.guestStudents} Gastschüler${worksheet.distributionCount.guestStudents !== 1 ? ':innen' : ':in'}`
          );
        }
        return parts.join('\n');
      })(),
      actionItem: (
        <WorksheetMenu
          items={[
            {
              icon: <NovaIcon icon={EditIcon} size="s" aria-hidden />,
              label: 'Bearbeiten',
              onClick: (e) => {
                e.stopPropagation(); // Prevent the row click event
                handleWsClick(worksheet.id);
              },
            },
            {
              icon: <NovaIcon icon={ContentCopyIcon} size="s" fill="none" aria-hidden />,
              label: 'Duplizieren',
              onClick: (e) => {
                e.stopPropagation(); // Prevent the row click event
                handleWsDuplicateClick(worksheet.id);
              },
            },
            {
              icon: <NovaIcon icon={ShareIcon} size="s" fill="none" aria-hidden />,
              label: 'Teilen',
              onClick: (e) => {
                e.stopPropagation(); // Prevent the row click event
                handleWsShareClick(worksheet.id);
              },
            },

            {
              icon: <NovaIcon icon={DeleteIcon} size="s" fill="none" aria-hidden />,
              label: 'Löschen',
              onClick: (e) => {
                e.stopPropagation(); // Prevent the row click event
                handleWsDeleteClick(worksheet.id);
              },
              dangerousAction: true,
            },
          ]}
        />
      ),
    };
  });

  const handleFilterChange = (filter: WorksheetDistributionFilter): void => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value);
    // Page reset will happen in the debounce effect
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  const totalPages = worksheets.data
    ? Math.ceil(worksheets.data.total / worksheets.data.pageSize)
    : 0;

  const hasWorksheets = !!(mappedWorksheets && mappedWorksheets.length > 0);

  const isFilterActive = activeFilter !== WorksheetDistributionFilter.ALL;
  const showNoWorksheetsState =
    !worksheets.isLoading && !hasWorksheets && !isFilterActive && !debouncedSearchQuery;
  const showNoResultsState =
    !worksheets.isLoading && !hasWorksheets && (isFilterActive || debouncedSearchQuery);

  const getListContent = (): JSX.Element => {
    if (showNoWorksheetsState) {
      return (
        <NoTableContent
          title="Du hast noch keine Arbeitsblätter."
          description="Erstelle ein Arbeitsblatt und weise es deinen Auszubildenden zu."
          onClick={handleCreateWorksheet}
          buttonText="Arbeitsblatt erstellen"
        />
      );
    }
    if (showNoResultsState) {
      return <NoSearchResult query={debouncedSearchQuery} />;
    }
    return (
      <>
        <Table
          columns={worksheetColumns}
          data={mappedWorksheets || []}
          isDataLoading={worksheets.isLoading}
          onRowClick={handleWsClick}
          tableRowProps={{
            cursor: 'pointer',
          }}
        />
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </>
    );
  };

  const showFilters = !showNoWorksheetsState;
  const worksheetViewTabs: TabData[] = [
    { id: 0, tabName: 'Meine Arbeitsblätter' },
    { id: 1, tabName: 'Arbeitsblätter meiner Organisation' },
  ];
  const activeWorksheetViewIndex = worksheetView === 'organization' ? 1 : 0;

  if (worksheets.isError) {
    return (
      <QueryErrorState error={worksheets.error} onRetry={() => worksheets.refetch()} />
    );
  }

  return (
    <VStack gap="l">
      <TabBar
        px="0"
        variant={TabBarVariant.WORKSHEET}
        tabs={worksheetViewTabs}
        activeTabIndex={activeWorksheetViewIndex}
        onTabChange={(index) => setWorksheetView(index === 1 ? 'organization' : 'my')}
      >
        <Tabs.Content value="Meine Arbeitsblätter" p="0" pt="l">
          <VStack gap="l" align="stretch">
            <HStack justifyContent="space-between" alignItems="center">
              <Text textStyle="h3.medium" color="text.primary">
                Arbeitsblätter
              </Text>

              <Button onClick={handleCreateWorksheet}>
                <NovaIcon icon={AddIcon} size="m" aria-hidden />
                Arbeitsblatt erstellen
              </Button>
            </HStack>
            {showFilters && (
              <>
                <Filters
                  filters={[
                    [
                      {
                        id: 'searchQuery',
                        element: InputOption,
                        placeholder: 'Suche nach Arbeitsblatt',
                        value: searchQuery,
                        options: [],
                        onChange: handleSearchChange,
                        icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                        onClear: handleClearSearch,
                      },
                    ],
                  ]}
                />
                <HStack gap="15px" px="0">
                  <Box
                    as="button"
                    bg={
                      activeFilter === WorksheetDistributionFilter.ALL
                        ? 'tag.background.green'
                        : 'transparent'
                    }
                    border="default"
                    borderColor={
                      activeFilter === WorksheetDistributionFilter.ALL
                        ? 'border.accent.active'
                        : 'border.accent'
                    }
                    px="10px"
                    py="5px"
                    borderRadius="5px"
                    cursor="pointer"
                    onClick={() => handleFilterChange(WorksheetDistributionFilter.ALL)}
                    _hover={{ opacity: '80' }}
                  >
                    <Text textStyle="body.s.regular" color="text.primary" lineHeight="1.3">
                      {`Alle (${worksheets.data?.filterCounts?.[WorksheetDistributionFilter.ALL] ?? 0})`}
                    </Text>
                  </Box>
                  <Box
                    as="button"
                    bg={
                      activeFilter === WorksheetDistributionFilter.DRAFT
                        ? 'tag.background.green'
                        : 'transparent'
                    }
                    border="default"
                    borderColor={
                      activeFilter === WorksheetDistributionFilter.DRAFT
                        ? 'border.accent.active'
                        : 'border.accent'
                    }
                    px="10px"
                    py="5px"
                    borderRadius="5px"
                    cursor="pointer"
                    onClick={() => handleFilterChange(WorksheetDistributionFilter.DRAFT)}
                    _hover={{ opacity: '80' }}
                  >
                    <Text textStyle="body.s.regular" color="text.secondary" lineHeight="1.3">
                      {`Entwurf (${worksheets.data?.filterCounts?.[WorksheetDistributionFilter.DRAFT] ?? 0})`}
                    </Text>
                  </Box>
                  <Box
                    as="button"
                    bg={
                      activeFilter === WorksheetDistributionFilter.SHARED
                        ? 'tag.background.green'
                        : 'transparent'
                    }
                    border="default"
                    borderColor={
                      activeFilter === WorksheetDistributionFilter.SHARED
                        ? 'border.accent.active'
                        : 'border.accent'
                    }
                    px="10px"
                    py="5px"
                    borderRadius="5px"
                    cursor="pointer"
                    onClick={() => handleFilterChange(WorksheetDistributionFilter.SHARED)}
                    _hover={{ opacity: '80' }}
                  >
                    <Text textStyle="body.s.regular" color="text.secondary" lineHeight="1.3">
                      {`Ausgeteilt (${worksheets.data?.filterCounts?.[WorksheetDistributionFilter.SHARED] ?? 0})`}
                    </Text>
                  </Box>
                </HStack>
              </>
            )}
            {getListContent()}
          </VStack>
        </Tabs.Content>
        <Tabs.Content value="Arbeitsblätter meiner Organisation" p="0" pt="l">
          <OrganizationWorksheetsTab onCreateTemplateClick={() => setWorksheetView('my')} />
        </Tabs.Content>
      </TabBar>
    </VStack>
  );
};
